import { CommonUIError } from '@afterpay/types'
import { isCallable, isNullOrUndefined } from '@afterpay/utils'
import clsx from 'clsx'
import { AnyMaskedOptions, createMask, MaskedDynamic } from 'imask'
import React, {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  Fragment,
  isValidElement,
  KeyboardEventHandler,
  memo,
  MouseEvent,
  ReactNode,
  Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { ZXCVBNResult } from 'zxcvbn'

import { ArcadeIconNames, Icon, IconNames, IconProps, isPaymentIcon, PaymentIcon, PaymentIconNames } from '../Icon'
import { Skeleton } from '../Skeleton'
import { useTheme, useThemeClass } from '../Theme'
import { Span } from '../Typography'
import { useCombinedRef } from '../useCombinedRef'
import { noop } from '../utils/function'
import { getFirstNonEmptyString } from '../utils/string'
import { ClusterPosition, ICON_POSITIONS, KINDS, SIZES } from './constants'
import styles from './style.module.scss'
import { mapThemedIconColor, mapThemedIconSize, mapThemedPasswordToggleIconSize, mapThemedPaymentIconSize, mapThemedShouldRenderErrorIcon } from './utils'

// TODO: validation preset

export type InputOnChange = ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
export type InputOnFocus = FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>
export type InputMouseEvent = MouseEvent<HTMLInputElement> | MouseEvent<HTMLTextAreaElement>
type AnimationEventTarget = HTMLInputElement | null

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
export type InputKinds = KindsType[KindsKeys]
export const InputKindsEnum = KINDS

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type InputSizes = SizesType[SizesKeys]

type IconPositionType = typeof ICON_POSITIONS
type IconPositionKeys = keyof IconPositionType
export type InputIconPositions = IconPositionType[IconPositionKeys]

export enum InputBorderOverride {
  DisabledLeftBorderRadius = 'DISABLED_LEFT_BORDER_RADIUS',
}
export interface InputProps extends CommonUIError, Omit<React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'onChange'> {
  autoComplete?: string
  autoFocus?: boolean
  autoSelectOnFocus?: boolean
  capitalize?: boolean
  characterLimit?: number
  clusterPosition?: ClusterPosition
  defaultPasswordMasked?: boolean
  disabled?: boolean
  format?: string
  fullWidth?: boolean
  icon?: IconNames | PaymentIconNames | ArcadeIconNames
  iconAriaLabel?: string
  passwordToggleAriaLabel?: string
  iconClickHandler?: () => void
  iconRef?: Ref<HTMLElement>
  iconPosition?: InputIconPositions
  iconProps?: IconProps
  showErrorIconWithCustomIcon?: boolean
  infoText?: string
  kind?: InputKinds
  label?: string
  secondaryLabel?: string
  maskOptions?: AnyMaskedOptions
  name?: string
  onBlur?: (e: InputOnFocus) => void
  onChange?: (e: InputOnChange, rawValue: string, viewValue: string) => void
  onMouseUp?: (e: InputMouseEvent) => void
  onMouseDown?: (e: InputMouseEvent) => void
  onPasswordMaskChange?: (state: boolean) => void
  onPasswordStrengthChange?: (value: number) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  override?: InputBorderOverride
  passwordStrengthLabel?: string[]
  pattern?: string
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  shrinkLabel?: boolean
  size?: InputSizes
  tabIndex?: number
  testNameSpace?: string
  value?: string
  'aria-disabled'?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'data-dd-action-name'?: string
  isLoading?: boolean
  controlled?: boolean
}

export type ZXCVBNFunction = (password: string, userInputs?: string[]) => ZXCVBNResult

export interface ZxcvbnType {
  default: ZXCVBNFunction
}

let passwordStrengthLib: ZXCVBNFunction | undefined

export const BaseInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      autoComplete = 'off',
      autoFocus = false,
      autoSelectOnFocus = true,
      capitalize = false,
      characterLimit,
      clusterPosition,
      defaultPasswordMasked = true,
      disabled = false,
      error = false,
      errorText,
      format = '',
      fullWidth = false,
      icon,
      iconAriaLabel,
      passwordToggleAriaLabel,
      iconClickHandler,
      iconRef,
      iconPosition = ICON_POSITIONS.RIGHT,
      infoText,
      kind = KINDS.TEXT,
      label = '',
      secondaryLabel = '',
      maskOptions,
      name = '',
      onBlur = noop,
      onChange = (event: InputOnChange) => event.target.value,
      onMouseUp = noop,
      onMouseDown = noop,
      onPasswordMaskChange = noop,
      onPasswordStrengthChange = noop,
      override,
      passwordStrengthLabel,
      readOnly = false,
      required = false,
      shrinkLabel,
      size = SIZES.LARGE,
      tabIndex = 0,
      testNameSpace = '',
      value = '',
      isLoading = false,
      showErrorIconWithCustomIcon = false,
      controlled = false,
      ...otherProps
    },
    ref
  ) => {
    const { theme } = useTheme()
    const themeClass = useThemeClass()
    const baseId = getFirstNonEmptyString(name, testNameSpace)
    const inputId = otherProps.id ?? (baseId ? `${baseId}-input` : undefined)
    const [charCount, setCharCount] = useState(value?.length ?? 0)
    const [isFilled, setIsFilled] = useState(value?.length > 0)
    const [isPasswordMasked, setPasswordMasked] = useState(defaultPasswordMasked)
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [isPasswordStrengthLibLoaded, setPasswordStrengthLibLoaded] = useState(false)
    const [shouldAutoSelect, setShouldAutoSelect] = useState(autoSelectOnFocus)

    const iconIsPaymentType = isPaymentIcon(icon)
    const iconSize = iconIsPaymentType ? mapThemedPaymentIconSize[theme.selected] : mapThemedIconSize[theme.selected]
    const hasMask = maskOptions !== undefined || format !== ''
    const dynamicMask = maskOptions !== undefined ? createMask(maskOptions) : null
    const staticMask =
      format !== ''
        ? createMask({
            mask: format,
          })
        : null
    const useMask = dynamicMask ?? staticMask

    const unsupportedSelectionRangeType = [KINDS.EMAIL, KINDS.NUMBER] as InputKinds[]
    const [inputValue, setInputValue] = useState(hasMask ? useMask?.resolve(value) : value)
    const [rawValue, setRawValue] = useState(value)
    const [isFocus, setFocus] = useState(false)
    const shouldRenderErrorIcon = mapThemedShouldRenderErrorIcon[theme.selected] && error

    const showSecondaryLabel = isFocus && !isFilled && secondaryLabel.length > 0

    const iconClass = clsx(
      kind !== KINDS.TEXTAREA && kind !== KINDS.PASSWORD && typeof icon !== 'undefined' && iconIsPaymentType && styles.hasPaymentIcon,
      kind !== KINDS.TEXTAREA && kind !== KINDS.PASSWORD && typeof icon !== 'undefined' && !iconIsPaymentType && styles.hasIcon,
      typeof icon !== 'undefined' && iconPosition && styles[`hasIconPosition${iconPosition}` as const]
    )
    const componentClassNames = clsx(
      styles.component,
      isFocus && clusterPosition !== undefined && styles.componentActive,
      clusterPosition !== undefined && styles[`componentCluster${clusterPosition}` as const],
      styles[`modSize${size}` as const],
      styles[`modKind${kind}` as const],
      disabled && styles.modDisabled,
      (showSecondaryLabel || isFilled || Boolean(shrinkLabel)) && styles.modFilled,
      kind === KINDS.PASSWORD && isPasswordMasked && styles.modMasked,
      error && styles.modError,
      isFocus && styles.modFocus,
      capitalize && styles.modCapitalize,
      iconClass,
      override !== undefined && override === InputBorderOverride.DisabledLeftBorderRadius && styles.disabledLeftBorderRadius,
      fullWidth && styles.fullWidth,
      styles[themeClass]
    )

    const labelClassNames = clsx(styles.label, styles[themeClass])

    const innerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
    const combinedRef = useCombinedRef(ref, innerRef)
    const hasTestId = testNameSpace.length > 0

    const isDynamicMask = dynamicMask != null
    const hasCurrentMask = isDynamicMask && 'currentMask' in dynamicMask
    const hasStaticMask = staticMask != null && 'mask' in staticMask && typeof staticMask.mask === 'string'

    const dynamicMaskString = hasCurrentMask ? ((dynamicMask as MaskedDynamic).currentMask?.mask as string) : ''
    const staticMaskString = hasStaticMask ? staticMask.mask : ''

    const viewCharacterLimit = hasMask ? (isDynamicMask ? (dynamicMaskString?.length ?? 0) + 1 : staticMaskString?.length ?? 0) : characterLimit

    const modelCharacterLimit = hasMask
      ? isDynamicMask
        ? (dynamicMaskString?.match(/[0a*]/g) ?? []).length + 1
        : (staticMaskString?.match(/0|a|\*/g) ?? []).length
      : characterLimit

    const hasErrorText: boolean = (typeof errorText === 'string' && errorText.length > 0) || isValidElement(errorText)
    const shouldApplyErrorId = error && hasErrorText && (name.length > 0 || testNameSpace.length > 0)
    const errorId = shouldApplyErrorId ? `${getFirstNonEmptyString(name, testNameSpace)}-error` : ''

    if (disabled) {
      tabIndex = -1
      otherProps['aria-disabled'] = true
    }

    if (kind === KINDS.NUMBER) {
      otherProps.pattern = '[0-9]*'
    }

    const getType = (): string => {
      switch (kind) {
        case KINDS.PASSWORD:
          return isPasswordMasked ? KINDS.PASSWORD : KINDS.TEXT
        default:
          return kind
      }
    }

    const togglePassword = (): void => {
      const newState = !isPasswordMasked
      onPasswordMaskChange(newState)
      setPasswordMasked(newState)
    }

    const onChangeHandler = (event: InputOnChange): void => {
      const newValue: string = event.target.value
      const isBadInput: boolean = !event.target.validity.valid && event.target.validity.badInput
      setIsFilled(isBadInput || (typeof newValue !== 'undefined' && newValue.length > 0))
      const currentCursorPosition = event.target.selectionStart
      if (useMask !== null) {
        useMask.unmaskedValue = event.target.dataset.rawdata ?? ''
        useMask.resolve(newValue)
      }

      const viewValue = useMask?.value ?? newValue
      const rawValue = useMask?.unmaskedValue ?? newValue

      setInputValue(viewValue)
      setRawValue(rawValue)
      setCharCount(rawValue.length)

      if (kind === KINDS.PASSWORD && isPasswordStrengthLibLoaded) {
        void (passwordStrengthLib !== null && passwordStrengthLib !== undefined && setPasswordStrength(passwordStrengthLib(newValue).score))
      }

      if (useMask !== null && currentCursorPosition !== null && currentCursorPosition < newValue.length) {
        const input = combinedRef.current
        setTimeout(() => {
          void (unsupportedSelectionRangeType.includes(kind) && input?.setAttribute('type', 'text'))
          input?.setSelectionRange(currentCursorPosition, currentCursorPosition)
          void (unsupportedSelectionRangeType.includes(kind) && input?.setAttribute('type', kind))
        }, 0)
      }

      onChange(event, rawValue, viewValue)
    }
    const onFocusHandler = (): void => {
      const input = combinedRef?.current
      if (autoSelectOnFocus) {
        void (unsupportedSelectionRangeType.includes(kind) && input?.setAttribute('type', 'text'))
        input?.setSelectionRange(0, 9999)
        void (unsupportedSelectionRangeType.includes(kind) && input?.setAttribute('type', kind))
      }
      setFocus(true)
    }
    const onBlurHandler = (event: InputOnFocus): void => {
      setFocus(false)
      onBlur(event)
    }

    const onMouseUpHandler = (event: InputMouseEvent): void => {
      if (autoSelectOnFocus && shouldAutoSelect) {
        event.preventDefault()
      }
      onMouseUp(event)
    }

    const onMouseDownHandler = (event: InputMouseEvent): void => {
      if (autoSelectOnFocus && event.target === combinedRef?.current) {
        setShouldAutoSelect(!isFocus)
      }
      onMouseDown(event)
    }

    const renderTextInput = (): ReactNode => {
      const inputClassNames = clsx(styles.input, clusterPosition !== undefined && styles[`inputCluster${clusterPosition}` as const], styles[`${themeClass}`])

      return (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={inputClassNames}
          id={inputId}
          {...(!otherProps['data-dd-action-name'] && { 'data-dd-action-name': `${testNameSpace} Input` })}
          {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })}
          disabled={disabled}
          maxLength={viewCharacterLimit}
          name={name}
          onChange={onChangeHandler}
          tabIndex={tabIndex}
          readOnly={readOnly}
          required={required}
          type={isLoading ? 'hidden' : getType()}
          value={inputValue}
          autoComplete={autoComplete}
          aria-label={secondaryLabel.length > 0 ? `${label} (${secondaryLabel})` : label}
          data-rawdata={rawValue}
          ref={combinedRef}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onMouseUp={onMouseUpHandler}
          onMouseDown={onMouseDownHandler}
          {...(shouldApplyErrorId && { 'aria-describedby': errorId })}
          {...otherProps}
        />
      )
    }

    const renderTextArea = (): ReactNode => {
      return (
        <textarea
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={styles.textArea}
          id={inputId}
          {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })}
          disabled={disabled}
          maxLength={viewCharacterLimit}
          name={name}
          onChange={onChangeHandler}
          readOnly={readOnly}
          required={required}
          tabIndex={tabIndex}
          value={inputValue}
          autoComplete={autoComplete}
          aria-label={label}
          data-rawdata={rawValue}
          ref={combinedRef}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onMouseUp={onMouseUpHandler}
          onMouseDown={onMouseDownHandler}
          {...otherProps}
        ></textarea>
      )
    }

    const renderInput = (): ReactNode => {
      switch (kind) {
        case KINDS.TEXTAREA:
          return renderTextArea()
        default:
          return renderTextInput()
      }
    }

    const renderPasswordToggle = (): ReactNode => {
      const IconComponent = memo(isPasswordMasked ? Icon.PasswordHide : Icon.PasswordShow)

      const iconColor = mapThemedIconColor[theme.selected][error ? 'error' : 'default']
      const passwordToggleClassNames = clsx(styles.passwordToggle, error && styles.passwordToggleError)

      return (
        <button
          {...(hasTestId && { 'data-testid': `${testNameSpace}-passwordToggle` })}
          type='button'
          onClick={togglePassword}
          className={passwordToggleClassNames}
          aria-label={passwordToggleAriaLabel ?? kind}
        >
          <IconComponent size={mapThemedPasswordToggleIconSize[theme.selected]} color={iconColor} />
        </button>
      )
    }

    const renderIcon = (): ReactNode => {
      if (isNullOrUndefined(icon)) {
        return null
      }
      if (showErrorIconWithCustomIcon && shouldRenderErrorIcon) {
        return null
      }
      const IconComponent = memo(iconIsPaymentType ? PaymentIcon : Icon)
      if (iconClickHandler != null) {
        return (
          <button
            onClick={iconClickHandler}
            onMouseDown={(e) => e.preventDefault()}
            ref={iconRef as Ref<HTMLButtonElement>}
            className={styles.iconContainer}
            aria-label={iconAriaLabel ?? `${testNameSpace} Icon action`}
            type='button'
            {...(hasTestId && { 'data-testid': `${testNameSpace}-iconWrapper` })}
          >
            <IconComponent {...otherProps.iconProps} name={icon} size={iconSize} />
          </button>
        )
      }
      return <IconComponent {...otherProps.iconProps} name={icon} size={iconSize} />
    }

    const renderHelper = (): ReactNode => {
      // TODO: account for links
      if (kind === KINDS.TEXTAREA) {
        return null
      }
      if (kind === KINDS.TEXT && typeof icon === 'undefined') {
        return null
      }
      const helperIcon = kind === KINDS.PASSWORD ? renderPasswordToggle() : renderIcon()

      return (
        <span {...(hasTestId && { 'data-testid': `${testNameSpace}-helper` })} className={styles.helper}>
          {helperIcon}
        </span>
      )
    }

    const renderErrorIcon = (): ReactNode => {
      const cashErrorIconStyle = clsx(styles.cashErrorIconWrapper)

      if (!shouldRenderErrorIcon) {
        return null
      }

      if (kind === KINDS.PASSWORD) {
        return null
      }

      if (!showErrorIconWithCustomIcon && !isNullOrUndefined(icon) && iconPosition === ICON_POSITIONS.RIGHT) {
        return null
      }

      return (
        <div className={cashErrorIconStyle} data-testid='input-error-icon'>
          <Icon name='Warning' color='Fire' size={iconSize} />
        </div>
      )
    }

    const renderInfo = (): ReactNode | null => {
      const hasInfoText: boolean = typeof infoText !== 'undefined' && infoText.length > 0
      const hasCharacterLimit: boolean = typeof characterLimit !== 'undefined' && characterLimit > 0
      const shouldRenderInfoText: boolean = hasErrorText || hasInfoText
      const shouldRenderCharacterLimit: boolean = !hasErrorText && hasCharacterLimit
      const shouldRenderInfo: boolean = shouldRenderInfoText || shouldRenderCharacterLimit
      const renderErrorText = isCallable(errorText) ? errorText() : errorText
      const displayError = error && hasErrorText

      if (!shouldRenderInfo) {
        return null
      }

      return (
        <div className={styles.info}>
          {shouldRenderInfoText && (
            <Span
              color={displayError ? 'Fire' : 'Gray40'}
              size='XS'
              {...(shouldApplyErrorId && { id: errorId })}
              {...(hasTestId && { testNameSpace: `${testNameSpace}-info` })}
              {...(error ? { 'aria-live': 'polite' } : {})}
            >
              {displayError ? renderErrorText : infoText}
            </Span>
          )}
          {shouldRenderCharacterLimit && (
            <Span size='XS' {...(hasTestId && { 'data-testid': `${testNameSpace}-info-count` })} className={styles.infoCount}>
              {charCount} / {modelCharacterLimit}
            </Span>
          )}
        </div>
      )
    }

    const renderPasswordStrengthLabel = (passwordStrength: number): ReactNode | null => {
      if (kind !== KINDS.PASSWORD || typeof passwordStrengthLabel === 'undefined' || passwordStrengthLabel.length < 1) {
        return null
      }
      const index = passwordStrength - 1 < 0 ? 0 : passwordStrength - 1
      const label = passwordStrengthLabel[index] ?? passwordStrengthLabel[passwordStrengthLabel.length - 1]
      return (
        <span className={styles.progressLabel} aria-live='polite'>
          {label}
        </span>
      )
    }

    const renderPasswordStrength = (): ReactNode | null => {
      if (
        kind !== KINDS.PASSWORD ||
        !isPasswordStrengthLibLoaded ||
        typeof passwordStrengthLabel === 'undefined' ||
        passwordStrengthLabel.length < 1 ||
        (typeof inputValue !== 'undefined' && inputValue.length < 1)
      ) {
        return null
      }
      const progressClassNames = clsx(
        styles.progress,
        passwordStrength <= 1 && styles.progressPoor,
        passwordStrength === 2 && styles.progressAverage,
        passwordStrength >= 3 && styles.progressGood
      )

      const wrapperClassNames = clsx({
        [styles.progressBarWrapper]: true,
        [styles.progressBarWrapperReveal]: typeof inputValue !== 'undefined' && inputValue.length > 0,
      })

      const mod = 100 / 3
      const progressBarWidth = passwordStrength === 0 ? `${mod}%` : `${passwordStrength * mod}%`
      return (
        <div className={wrapperClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-passwordStrength` })}>
          <div className={styles.progressBar}>
            <span className={styles.bar}>
              <span className={progressClassNames} style={{ width: progressBarWidth }}></span>
            </span>
          </div>
          {renderPasswordStrengthLabel(passwordStrength)}
        </div>
      )
    }

    const loadPasswordStrengthLib = async (): Promise<ZXCVBNFunction> => (await import('zxcvbn')).default

    useEffect(() => {
      if (kind === KINDS.PASSWORD) {
        if (!isPasswordStrengthLibLoaded) {
          loadPasswordStrengthLib().then(
            (pkg) => {
              setPasswordStrengthLibLoaded(true)
              passwordStrengthLib = pkg
              setPasswordStrength(typeof inputValue !== 'undefined' ? passwordStrengthLib(inputValue).score : 0)
            },
            () => {}
          )
        } else {
          void (
            passwordStrengthLib !== null &&
            passwordStrengthLib !== undefined &&
            setPasswordStrength(typeof inputValue !== 'undefined' ? passwordStrengthLib(inputValue).score : 0)
          )
        }
      }
    }, [kind, inputValue, isPasswordStrengthLibLoaded])

    useEffect(() => {
      onPasswordStrengthChange(passwordStrength)
    }, [passwordStrength, onPasswordStrengthChange])

    useLayoutEffect(() => {
      const handleAutoFill = (e: AnimationEvent): void => {
        switch (e.animationName) {
          case styles.onAutoFillStart: {
            setIsFilled(true)
            break
          }
          case styles.onAutoFillCancel: {
            const target = e.target as AnimationEventTarget
            const isFilled = value?.length !== 0 || target?.value?.length !== 0
            setIsFilled(isFilled)
            break
          }
        }
      }
      const input = combinedRef.current
      if (!isNullOrUndefined(input)) {
        input.addEventListener('animationstart', handleAutoFill as EventListener)
      }
      return () => {
        if (!isNullOrUndefined(input)) {
          input.removeEventListener('animationstart', handleAutoFill as EventListener)
        }
      }
    }, [value, combinedRef])

    useEffect(() => {
      if (!controlled) return
      const resolved = hasMask ? useMask?.resolve(value) : value
      setInputValue(resolved)
      setRawValue(value)
      setIsFilled(value.length > 0)
    }, [controlled, value])

    return (
      <div className={componentClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-wrapper` })}>
        <Fragment key={String(theme.isDarkModeActive)}>
          <label htmlFor={inputId} className={labelClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-label` })}>
            {label}
          </label>
          {showSecondaryLabel && (
            <span className={styles.secondaryLabel} {...(hasTestId && { 'data-testid': `${testNameSpace}-secondary-label` })}>
              {secondaryLabel}
            </span>
          )}
          <div>
            <div className={styles.inputWrapper}>
              {isLoading && (
                <div className={styles.skeletonWrapper}>
                  <Skeleton testNameSpace={'skeleton-loader'} />
                </div>
              )}
              {renderInput()}
              {!isLoading && (
                <>
                  {renderHelper()}
                  {renderErrorIcon()}
                </>
              )}
            </div>
            {renderPasswordStrength()}
            {renderInfo()}
          </div>
        </Fragment>
      </div>
    )
  }
)

const Text = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.TEXT} {...otherProps} ref={ref} />
})

export const TextArea = forwardRef<HTMLTextAreaElement, InputProps & { rows?: number }>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.TEXTAREA} {...otherProps} ref={ref} />
})
const Password = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.PASSWORD} {...otherProps} ref={ref} />
})

const Number = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.NUMBER} {...otherProps} ref={ref} />
})

const Tel = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.TEL} {...otherProps} ref={ref} />
})

const Email = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.EMAIL} {...otherProps} ref={ref} />
})

const Url = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { kind, ...otherProps } = props

  return <BaseInput kind={KINDS.URL} {...otherProps} ref={ref} />
})

export type InputComponent = typeof BaseInput & {
  Text: typeof Text
  TextArea: typeof TextArea
  Password: typeof Password
  Number: typeof Number
  Tel: typeof Tel
  Email: typeof Email
  Url: typeof Url
}

export const Input = BaseInput as InputComponent

Input.displayName = 'Input'
Text.displayName = 'Input.Text'
TextArea.displayName = 'TextArea'
Password.displayName = 'Input.Password'
Number.displayName = 'Input.Number'
Tel.displayName = 'Input.Tel'
Email.displayName = 'Input.Email'
Url.displayName = 'Input.Url'

Input.Text = Text
Input.TextArea = TextArea
Input.Password = Password
Input.Number = Number
Input.Tel = Tel
Input.Email = Email
Input.Url = Url
